import { useEffect, useState } from 'react';
import { supabase } from '../supabaseClient';

/** One plank per day: a note done is 'laid', a check-in alone is 'touched'. */
export type Plank = 'laid' | 'touched' | 'empty' | 'future';

export interface JourneyWeek {
  start: Date;
  planks: Plank[]; // Sunday → Saturday
  laid: number;
}

const WEEKS = 8;

const dayKey = (d: Date) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;

export const weekStart = (d: Date) => {
  const s = new Date(d);
  s.setHours(0, 0, 0, 0);
  s.setDate(s.getDate() - s.getDay());
  return s;
};

/** The last weeks as bridges, newest first. Missed days are just gaps, never penalties. */
export function useWeeklyJourney(userId?: string | null, refreshKey = 0) {
  const [weeks, setWeeks] = useState<JourneyWeek[] | null>(null);

  useEffect(() => {
    if (!userId) return;
    let alive = true;
    const thisWeek = weekStart(new Date());
    const from = new Date(thisWeek);
    from.setDate(from.getDate() - 7 * (WEEKS - 1));

    Promise.all([
      supabase.from('xp_history').select('created_at').eq('user_id', userId).gte('created_at', from.toISOString()),
      supabase.from('mood_logs').select('created_at').eq('user_id', userId).gte('created_at', from.toISOString()),
    ]).then(([xp, mood]: { data: { created_at: string }[] | null }[]) => {
      if (!alive) return;
      const laid = new Set((xp.data ?? []).map((r) => dayKey(new Date(r.created_at))));
      const touched = new Set((mood.data ?? []).map((r) => dayKey(new Date(r.created_at))));
      const today = new Date();
      const out: JourneyWeek[] = [];
      for (let w = 0; w < WEEKS; w++) {
        const start = new Date(thisWeek);
        start.setDate(start.getDate() - 7 * w);
        const planks: Plank[] = Array.from({ length: 7 }, (_, i) => {
          const d = new Date(start);
          d.setDate(d.getDate() + i);
          if (d > today) return 'future';
          const k = dayKey(d);
          return laid.has(k) ? 'laid' : touched.has(k) ? 'touched' : 'empty';
        });
        out.push({ start, planks, laid: planks.filter((p) => p === 'laid').length });
      }
      // Weeks before the very first plank are not part of the journey yet.
      let last = out.length - 1;
      while (last > 0 && out[last].planks.every((p) => p === 'empty')) last--;
      setWeeks(out.slice(0, last + 1));
    });
    return () => { alive = false; };
  }, [userId, refreshKey]);

  return weeks;
}
